import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import JaxFormFields from "./JaxFormFields";
import { LoadingButton } from "@mui/lab";
import JaxDocs from "./JaxDocs";
import ScheduleOfValues from "./ScheduleOfValues";
import {
  fetchJaxFields,
  fetchSovTemplates,
  handleDownloadScheduleForm,
  handleCreateSchedule,
  handleSaveScheduleValuesTemplate,
  handleUpdateSpecsManually,
  updateScheduleValuesSignedUrl,
  revertChangedCommonValues,
  uploadDocumentJaxManually,
  setScheduleOfValues,
  setUsingTemplates,
  setSelectedRow,
} from "../../../store/actions/uiActions/myPermitsActions";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { useFormik } from "formik";
import * as Yup from "yup";

const JaxModal = ({ handleClosePanel, startCoords, animateOut }) => {
  const {
    jaxFieldsLoading,
    jaxFieldsFetchError,
    viewDocLoading,
    createScheduleLoading,
  } = useSelector((state) => state.myPermitsComponent.citySite);

  const { selectedRow, commonValues } = useSelector(
    (state) => state.myPermitsComponent
  );
  const { uploadDocLoading } = useSelector(
    (state) => state.myPermitsComponent.permits.loadings
  );
  const { isUsingTemplate, templateName } = useSelector(
    (state) => state.myPermitsComponent.usingTemplate
  );
  const {
    rows,
    scheduleCreated,
    editSchedule,
    downloadLoading,
    savingTemplateLoading,
    loadedLogo,
  } = useSelector((state) => state.myPermitsComponent.scheduleOfValues);

  const { jaxFields } = useSelector(
    (state) => state.myPermitsComponent.citySite
  );

  const [category, setCategory] = useState("specs");
  const [specsChanged, setSpecsChanged] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [readyToDisplay, setReadyToDisplay] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [tempTemplateName, setTempTemplateName] = useState("");
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [grouped, setGrouped] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [disableJobCost, setDisableJobCost] = useState(true);
  const [userHasSelected, setUserHasSelected] = useState(false);

  const saveValuesInputRef = useRef();

  const dispatch = useDispatch();

  useEffect(() => {
    if (category === "schedule") {
      if (downloadLoading) {
        setIsSubmitDisabled(true);
      } else if (scheduleCreated && !editSchedule) {
        setIsSubmitDisabled(false);
      } else if (rows?.length === 0) {
        setIsSubmitDisabled(true);
      } else {
        setIsSubmitDisabled(false);
      }
    } else if (category === "specs") {
      if (!specsChanged) {
        setIsSubmitDisabled(true);
      } else {
        setIsSubmitDisabled(false);
      }
    } else if (category === "documents") {
      if (!selectedFile) {
        setIsSubmitDisabled(true);
      } else {
        setIsSubmitDisabled(false);
      }
    } else {
      setIsSubmitDisabled(false);
    }
  }, [
    category,
    downloadLoading,
    editSchedule,
    rows?.length,
    scheduleCreated,
    selectedFile,
    specsChanged,
  ]);

  const initialValues = useMemo(() => {
    if (!jaxFields) return {};

    const values = {};

    jaxFields.forEach((f) => {
      const parentValue = f.value ?? "";
      values[f.name] = parentValue;

      if (f.child?.options) {
        const activeChildren = f.child.options?.[parentValue] || [];
        activeChildren.forEach((child) => {
          values[child.name] = child.value ?? "";
        });
      }
    });

    return values;
  }, [jaxFields]);

  const validationSchema = useMemo(() => {
    if (!jaxFields) return Yup.object().shape({});
    const shape = {};

    jaxFields.forEach((field) => {
      // Skip validation for job_cost field when Schedule of Values is selected
      if (field.name === "job_cost" && disableJobCost) {
        shape[field.name] = Yup.mixed().nullable();
        return;
      }

      switch (field.type) {
        case "text":
          shape[field.name] = Yup.string().nullable();
          break;
        case "int":
        case "float":
          shape[field.name] = Yup.number().nullable();
          break;
        case "bool":
        case "select":
          shape[field.name] = Yup.mixed().nullable();
          break;
        default:
          shape[field.name] = Yup.mixed().nullable();
          break;
      }
    });

    return Yup.object().shape(shape);
  }, [jaxFields, disableJobCost]);

  const formik = useFormik({
    enableReinitialize: true, // allows update when jaxFields arrive
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const allFields = Object.values(grouped || {}).flat();

      // Step 1: Build map of city_field_name => value
      const mappedValues = {};
      const mappedKeys = new Set();

      allFields.forEach((field) => {
        const key = field.city_field_name || field.name;
        if (key) {
          mappedValues[key] = values[field.name];
          mappedKeys.add(field.name);
        }

        // Also check for children
        if (field.child) {
          const childKey = field.child.city_field_name || field.child.name;
          if (childKey) {
            mappedValues[childKey] = values[field.child.name];
            mappedKeys.add(field.child.name);
          }
        }
      });

      // Step 2: Add remaining formik values that weren't mapped
      Object.entries(values).forEach(([key, val]) => {
        if (!mappedKeys.has(key)) {
          mappedValues[key] = val;
        }
      });

      await dispatch(handleUpdateSpecsManually(mappedValues));
    },
  });

  const groupByCategory = (fields) => {
    const grouped = {};
    fields.forEach((field) => {
      const category = field.categories?.[0] || "uncategorized";
      if (!grouped[category]) grouped[category] = [];
      grouped[category].push(field);
    });

    return grouped;
  };

  useEffect(() => {
    if (jaxFields?.length) {
      const group = groupByCategory(jaxFields);
      setGrouped(group);
      const groupKeys = Object.keys(group);
      setExpanded(groupKeys.length > 0 ? groupKeys[0] : null);

      // === Extract relevant values based on grouped fields ===
      const flatFields = Object.values(group)
        .flat()
        .map((f) => f.name);

      const visibleValues = {};
      flatFields.forEach((name) => {
        visibleValues[name] = formik.values?.[name] ?? null;
      });

      // === SEND TO BACKEND ===
      // You can dispatch or call an API here
      // await dispatch(sendVisibleFields(visibleValues));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jaxFields]);

  useEffect(() => {
    setSpecsChanged(formik.dirty);
  }, [formik.dirty]);

  useEffect(() => {
    if (category === "schedule") {
      dispatch(fetchSovTemplates());
    }
  }, [category, dispatch]);

  // Reset user selection when selectedRow changes
  useEffect(() => {
    setUserHasSelected(false);
  }, [selectedRow?.id]);

  // Initialize disableJobCost based on selectedRow data only if user hasn't made a selection
  useEffect(() => {
    if (!userHasSelected) {
      const need_create =
        selectedRow?.connect_to_city?.schedule_of_values?.need_create;
      if (need_create === null || need_create === undefined) {
        setDisableJobCost(true);
      } else {
        setDisableJobCost(
          selectedRow?.connect_to_city?.schedule_of_values?.need_create
        );
      }
    }
  }, [
    selectedRow?.connect_to_city?.schedule_of_values?.need_create,
    userHasSelected,
  ]);

  // Update disableJobCost when Redux store changes (even after user selection)
  useEffect(() => {
    const need_create =
      selectedRow?.connect_to_city?.schedule_of_values?.need_create;
    if (need_create !== null && need_create !== undefined) {
      setDisableJobCost(need_create);
    }
  }, [
    selectedRow?.connect_to_city?.schedule_of_values?.need_create,
    disableJobCost,
  ]);

  // Sync Redux commonValues.job_cost with formik when it changes
  useEffect(() => {
    if (Array.isArray(commonValues)) {
      const jobCostField = commonValues.find(
        (item) => item.common_name === "job_cost"
      );
      if (
        jobCostField?.value !== undefined &&
        formik.values.job_cost !== jobCostField.value
      ) {
        formik.setFieldValue("job_cost", jobCostField.value);
      }
    }
  }, [commonValues, formik]);

  const resetScheduleOfValuesState = () => {
    dispatch(
      setScheduleOfValues({
        rows: [],
        scheduleCreated: false,
        remainingAmount: "―",
        remainingPercentage: "―",
        totalCost: 0,
        totalCostDisplay: "",
        editSchedule: false,
        scheduleOfValuesDocLoading: false,
        loadedLogo: "",
        downloadLoading: false,
        savingTemplateLoading: false,
      })
    );

    // Also clear the template state
    dispatch(
      setUsingTemplates({
        isUsingTemplate: false,
        templateID: "",
        templateName: "",
        removedTemplate: false,
      })
    );
  };

  const handleSubmit = async () => {
    if (category === "documents") {
      if (selectedFile?.type !== "application/pdf") {
        setOpenPopup(true);
        return;
      }

      const resp = await dispatch(
        uploadDocumentJaxManually(selectedFile, selectedDocId, setSelectedDocId)
      );

      if (resp?.success) {
        setSelectedDocId("");
        setSelectedFile(null);
      } else {
        setOpenPopup(true);
      }
    } else if (category === "specs") {
      formik.handleSubmit();
    } else if (category === "schedule") {
      if (scheduleCreated && !editSchedule) {
        // Enable edit mode instead of creating new document
        await dispatch(
          setScheduleOfValues({
            editSchedule: true,
          })
        );
      } else {
        // Create or update the Schedule of Values document
        const resp = await dispatch(handleCreateSchedule(loadedLogo || null));
        if (resp?.success) {
          await dispatch(setSelectedRow(resp.data));
          await dispatch(
            setScheduleOfValues({
              scheduleCreated: true,
              editSchedule: false,
            })
          );
          // Refresh signed URLs so the preview image renders immediately
          // await dispatch(updateScheduleValuesSignedUrl());
          // Also refresh SOV templates so state matches post-create (fixes stale image state)
          await dispatch(fetchSovTemplates());
        } else {
          setOpenPopup(true);
        }
      }
    }
  };

  useEffect(() => {
    return () => {
      setSpecsChanged(false);
      setSelectedDocId("");
      setSelectedFile(null);
      setReadyToDisplay(false);
    };
  }, []);

  useEffect(() => {
    const timeOut = setTimeout(() => {
      setReadyToDisplay(true);
      return () => clearTimeout(timeOut);
    }, 1500);
  }, []);

  const startDownload = async () => {
    await dispatch(handleDownloadScheduleForm());
  };

  const submitSaveTemplate = async () => {
    const saved = await dispatch(
      handleSaveScheduleValuesTemplate(tempTemplateName)
    );
    if (saved) {
      setSavingTemplate(false);
      setTempTemplateName("");
    }
  };

  return (
    <>
      <Box
        className="jax-bubble-wrapper"
        onClick={() => {
          if (viewDocLoading) return;
          if (downloadLoading) return;
          if (category === "schedule" && savingTemplate) {
            setOpenPopup(true);
            return;
          }
          if (
            specsChanged ||
            selectedDocId ||
            (!scheduleCreated && rows?.length > 0) ||
            (scheduleCreated && editSchedule)
          ) {
            setOpenPopup(true);
          } else {
            handleClosePanel();
          }
        }}
      >
        <Box
          className={`jax-bubble-modal ${animateOut ? "exit" : ""}`}
          style={{
            "--start-top": `${startCoords.top}px`,
            "--start-left": `${startCoords.left}px`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {!animateOut && (
            <Box
              className={`jax-panel-inner ${!animateOut ? "show" : ""}`}
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                maxHeight: "100vh",
                p: "0px !important",
              }}
            >
              {/* Sticky Header */}
              <Box
                sx={{
                  backgroundColor: "#f3f3f3",
                  p: "18px !important",
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  borderBottom: "1px solid #ddd",
                }}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                {jaxFieldsLoading || jaxFieldsFetchError || !readyToDisplay ? (
                  <Typography sx={{ flex: 1 }} color="primary" variant="h6">
                    City Permit Details
                  </Typography>
                ) : (
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography color="primary" variant="h6">
                      City Permit Details \
                    </Typography>

                    <Breadcrumbs separator="›" aria-label="breadcrumb">
                      <Typography
                        onClick={() => {
                          if (viewDocLoading) return;
                          if (downloadLoading) return;
                          if (category === "schedule" && savingTemplate) {
                            setOpenPopup(true);
                            return;
                          }
                          if (
                            selectedDocId ||
                            (!scheduleCreated && rows?.length > 0) ||
                            (scheduleCreated && editSchedule)
                          ) {
                            setOpenPopup(true);
                          } else {
                            setCategory("specs");
                          }
                        }}
                        sx={{
                          cursor: "pointer",
                          fontWeight: category === "specs" ? "bold" : "normal",
                          color:
                            category === "specs"
                              ? "primary.main"
                              : "text.primary",
                        }}
                      >
                        Specs
                      </Typography>
                      <Typography
                        onClick={() => {
                          // if (viewDocLoading) return;
                          // if (downloadLoading) return;
                          // if (category === "schedule" && savingTemplate) {
                          //   setOpenPopup(true);
                          //   return;
                          // }
                          // if (
                          //   specsChanged ||
                          //   (!scheduleCreated && rows?.length > 0) ||
                          //   (scheduleCreated && editSchedule)
                          // ) {
                          //   setOpenPopup(true);
                          // } else {
                          //   setCategory("documents");
                          // }
                        }}
                        sx={{
                          // cursor: "pointer",
                          cursor: "default",
                          fontWeight:
                            category === "documents" ? "bold" : "normal",
                          color: "text.secondary",
                          // category === "documents"
                          //   ? "primary.main"
                          //   : "text.primary",
                        }}
                      >
                        Documents
                      </Typography>

                      <Typography
                        onClick={() => {
                          if (viewDocLoading) return;
                          if (downloadLoading) return;
                          if (category === "schedule" && savingTemplate) {
                            setOpenPopup(true);
                            return;
                          }
                          if (specsChanged || selectedDocId) {
                            setOpenPopup(true);
                          } else {
                            setCategory("schedule");
                          }
                        }}
                        sx={{
                          cursor: "pointer",
                          fontWeight:
                            category === "schedule" ? "bold" : "normal",
                          color:
                            category === "schedule"
                              ? "primary.main"
                              : "text.primary",
                        }}
                      >
                        Schedule of Values
                      </Typography>
                    </Breadcrumbs>
                  </Box>
                )}

                {jaxFieldsFetchError && (
                  <Tooltip title="Refresh">
                    <IconButton
                      onClick={() => {
                        if (viewDocLoading) return;
                        dispatch(fetchJaxFields());
                      }}
                    >
                      <RefreshIcon
                        className={jaxFieldsLoading ? "rotate" : ""}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                <IconButton
                  onClick={() => {
                    if (viewDocLoading) return;
                    if (downloadLoading) return;
                    if (category === "schedule" && savingTemplate) {
                      setOpenPopup(true);
                      return;
                    }
                    if (
                      specsChanged ||
                      selectedDocId ||
                      (!scheduleCreated && rows?.length > 0) ||
                      (scheduleCreated && editSchedule)
                    ) {
                      setOpenPopup(true);
                    } else {
                      handleClosePanel();
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Loading/Error/Content */}
              {jaxFieldsLoading || !readyToDisplay ? (
                <Box
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <CircularProgress size={50} />
                  <Box pl={1} pt={1}>
                    <Typography>Please Wait...</Typography>
                  </Box>
                </Box>
              ) : jaxFieldsFetchError ? (
                <Box
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Box pl={1} pt={1}>
                    <Typography variant="body1" fontWeight="bold" color="error">
                      {jaxFieldsFetchError}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <>
                  {/* Scrollable Form Content */}
                  <Box
                    sx={{
                      flexGrow: 1,
                      overflowY: "auto",
                      px: category === "schedule" ? 0 : 2,
                      py: category === "schedule" ? 0 : 2,
                      minHeight: 0,
                    }}
                  >
                    {category === "specs" ? (
                      <JaxFormFields
                        setCategory={setCategory}
                        formik={formik}
                        grouped={grouped}
                        expanded={expanded}
                        setExpanded={setExpanded}
                        disableJobCost={disableJobCost}
                        setDisableJobCost={setDisableJobCost}
                        setUserHasSelected={setUserHasSelected}
                      />
                    ) : category === "documents" ? (
                      <JaxDocs
                        selectedDocId={selectedDocId}
                        setSelectedDocId={setSelectedDocId}
                        selectedFile={selectedFile}
                        setSelectedFile={setSelectedFile}
                      />
                    ) : category === "schedule" ? (
                      <ScheduleOfValues
                        addRowMax={false}
                        setAddRowMax={() => {}}
                        setFile={setSelectedFile}
                        savingTemplate={savingTemplate}
                      />
                    ) : (
                      <></>
                    )}
                  </Box>

                  {/* Sticky Footer */}
                  <Box
                    sx={{
                      position: "sticky",
                      bottom: 0,
                      backgroundColor: "#fff",
                      borderTop:
                        category === "schedule"
                          ? "2px solid #004976"
                          : "1px solid #ddd",
                      py: 2,
                      px: category === "schedule" ? 0 : 2,
                      mx: category === "schedule" ? 2 : 0,
                      display: "flex",
                      justifyContent: "center",
                      gap: 2,
                    }}
                  >
                    {(category === "schedule" &&
                      scheduleCreated &&
                      !editSchedule) ||
                    (category === "schedule" && savingTemplate) ? (
                      <></>
                    ) : (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          if (viewDocLoading) return;
                          if (category === "schedule") {
                            if (scheduleCreated && editSchedule) {
                              // Cancel Edit - just revert to preview mode
                              dispatch(
                                setScheduleOfValues({
                                  editSchedule: false,
                                })
                              );
                            } else {
                              // Clear Values - reset everything
                              resetScheduleOfValuesState();
                            }
                            setSavingTemplate(false);
                            setTempTemplateName("");
                            // Don't close modal for "Clear Values" - just clear the data
                            return;
                          }
                          handleClosePanel();
                          dispatch(revertChangedCommonValues());
                        }}
                      >
                        {scheduleCreated && editSchedule
                          ? "Cancel Edit"
                          : rows?.length > 0 && category === "schedule"
                            ? "Clear Values"
                            : "Cancel"}
                      </Button>
                    )}

                    {!savingTemplate && (
                      <LoadingButton
                        loading={
                          Boolean(createScheduleLoading) ||
                          Boolean(uploadDocLoading)
                        }
                        onClick={handleSubmit}
                        disabled={Boolean(isSubmitDisabled)}
                        sx={{ minWidth: "10rem" }}
                        variant="contained"
                        size="small"
                        color={
                          category === "schedule" &&
                          scheduleCreated &&
                          !editSchedule
                            ? "warning"
                            : "primary"
                        }
                        endIcon={
                          category === "schedule" &&
                          scheduleCreated &&
                          !editSchedule ? (
                            <EditIcon />
                          ) : undefined
                        }
                      >
                        {category === "specs"
                          ? "Save"
                          : category === "documents"
                            ? "Upload"
                            : category === "schedule" && !scheduleCreated
                              ? "Create Document"
                              : category === "schedule" &&
                                  scheduleCreated &&
                                  !editSchedule
                                ? "Edit Document"
                                : category === "schedule" &&
                                    scheduleCreated &&
                                    editSchedule
                                  ? "Save Changes"
                                  : "Save"}
                      </LoadingButton>
                    )}

                    {category === "schedule" &&
                    scheduleCreated &&
                    !editSchedule ? (
                      <LoadingButton
                        loading={downloadLoading}
                        endIcon={<DownloadIcon />}
                        size="small"
                        variant="contained"
                        onClick={startDownload}
                      >
                        Download Document
                      </LoadingButton>
                    ) : (
                      category === "schedule" && (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          {savingTemplate && (
                            <Box>
                              <TextField
                                disabled={savingTemplateLoading}
                                inputRef={(el) => {
                                  if (!saveValuesInputRef.current)
                                    saveValuesInputRef.current = {};
                                  saveValuesInputRef.current.title = el;
                                }}
                                label="Enter Template Name"
                                variant="standard"
                                size="small"
                                value={tempTemplateName}
                                onChange={(e) => {
                                  setTempTemplateName(e.target.value);
                                }}
                                slotProps={{
                                  inputLabel: {
                                    shrink: true,
                                  },
                                  input: {
                                    sx: {
                                      padding: 0,
                                      fontSize: "0.85rem",
                                      height: "30px", // control height tightly
                                    },
                                  },
                                }}
                                sx={{
                                  mt: 1,
                                  "& .MuiInputBase-root": {
                                    height: "32px",
                                  },
                                }}
                              />
                            </Box>
                          )}

                          <Box>
                            <LoadingButton
                              loading={savingTemplateLoading}
                              disabled={
                                rows?.length <= 0 ||
                                (savingTemplate &&
                                  (!tempTemplateName ||
                                    (tempTemplateName &&
                                      tempTemplateName.length < 4)))
                              }
                              onClick={() => {
                                if (!savingTemplate) {
                                  if (isUsingTemplate && templateName) {
                                    setTempTemplateName(templateName);
                                  }
                                  setSavingTemplate(true);

                                  setTimeout(() => {
                                    const input =
                                      saveValuesInputRef.current?.title;
                                    if (input) {
                                      input.focus();
                                    }
                                  }, 100);
                                } else {
                                  submitSaveTemplate();
                                }
                              }}
                              variant={
                                savingTemplate ? "contained" : "outlined"
                              }
                              size="small"
                              color="success"
                              endIcon={<AddIcon />}
                            >
                              {savingTemplate ? "Submit" : "Save Values"}
                            </LoadingButton>
                          </Box>
                          {savingTemplate && (
                            <Button
                              disabled={savingTemplateLoading}
                              onClick={() => {
                                setSavingTemplate(false);
                                setTempTemplateName("");
                              }}
                              variant="outlined"
                              color="error"
                              size="small"
                              fontSize="small"
                            >
                              Cancel
                            </Button>
                          )}
                        </Box>
                      )
                    )}
                  </Box>
                </>
              )}
            </Box>
          )}
          <Snackbar
            open={openPopup}
            onClose={() => {
              setOpenPopup(false);
            }}
            autoHideDuration={category === "schedule" ? 8000 : 4000}
            anchorOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
          >
            <Alert
              severity="error"
              sx={{
                width: "100%",
                alignItems: "center",
                backgroundColor: (theme) =>
                  theme.palette.error.light || "#c62828",
                color: "#F77A71",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="body2" fontWeight="bold">
                  {category === "documents" &&
                  selectedFile?.type &&
                  selectedFile?.type !== "application/pdf"
                    ? "Only PDF files are allowed."
                    : "You’ve made changes. Don’t forget to save!"}
                </Typography>
                <Button
                  onClick={() => {
                    if (viewDocLoading) return;
                    setOpenPopup(false);
                  }}
                  sx={{ ml: 1 }}
                  color="error"
                  variant="text"
                  size="small"
                >
                  Close
                </Button>
              </Box>
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    </>
  );
};

export default React.memo(JaxModal);
